import React, { useState, useContext } from 'react';
import './Reports.css';
import { FaFilePdf, FaFilter, FaRedo, FaCalendarAlt, FaChartLine, FaDollarSign, FaUsers, FaStar } from 'react-icons/fa';
// Assuming UrlContext is available and provides the base URL
import { UrlContext } from '../../Context/UrlContext'; 


const REPORT_OPTIONS = [
    { id: 'sales', name: 'Sales Overview', icon: <FaDollarSign />, description: 'Total revenue, average order value, and order count.' },
    { id: 'inventory', name: 'Inventory Summary', icon: <FaChartLine />, description: 'Current stock levels and low stock items.' },
    { id: 'products', name: 'Product Performance', icon: <FaChartLine />, description: 'Best and worst selling products by revenue and units.' },
    { id: 'orders', name: 'Detailed Orders', icon: <FaCalendarAlt />, description: 'List of all placed orders with status and details.' },
    { id: 'users', name: 'User & Admin List', icon: <FaUsers />, description: 'List of all users, filtered by role and sign-up date.' },
    { id: 'reviews', name: 'Review Metrics', icon: <FaStar />, description: 'Average rating, count, and product review comparison.' },
];

const STATUS_OPTIONS = ['All', 'Pending', 'Processing', 'Shipped', 'Delivered', 'Cancelled'];
const ROLE_OPTIONS = ['All', 'user', 'admin'];

function Reports() {
    const { apiBaseUrl } = useContext(UrlContext);
    const API_BASE_URL = `${apiBaseUrl}/reports`;

    const [reportType, setReportType] = useState(REPORT_OPTIONS[0].id);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [productFilter, setProductFilter] = useState('');
    const [userRoleFilter, setUserRoleFilter] = useState('All');
    const [isGenerating, setIsGenerating] = useState(false);
    const [reportData, setReportData] = useState(null); 
    const [fetchError, setFetchError] = useState(null);

    // --- Core Logic for Report Generation (Uses Backend) ---
    const handleGenerateReport = async () => {
        setIsGenerating(true);
        setReportData(null); 
        setFetchError(null);

        // 1. Construct Query Parameters
        const params = new URLSearchParams({ type: reportType });
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);
        if (statusFilter !== 'All') params.append('status', statusFilter);
        if (userRoleFilter !== 'All') params.append('role', userRoleFilter);
        if (productFilter) params.append('productId', productFilter); 

        try {
            const response = await fetch(`${API_BASE_URL}/generate?${params.toString()}`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ error: 'Failed to parse error response.' }));
                throw new Error(errorData.error || `HTTP error! Status: ${response.status}`);
            }

            const data = await response.json();
            setReportData(data);

        } catch (error) {
            console.error('Error generating report:', error);
            setFetchError(error.message || 'An unknown error occurred.');
            setReportData(null);
        } finally {
            setIsGenerating(false);
        }
    };

    const handleDownloadPdf = () => {
        if (!reportData) return;
        
        const params = new URLSearchParams({ type: reportType });
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);
        if (statusFilter !== 'All') params.append('status', statusFilter);
        if (userRoleFilter !== 'All') params.append('role', userRoleFilter);
        if (productFilter) params.append('productId', productFilter);
        
        window.open(`${API_BASE_URL}/download?${params.toString()}`, '_blank');
    };

    const handleResetFilters = () => {
        setStartDate('');
        setEndDate('');
        setStatusFilter('All');
        setProductFilter('');
        setUserRoleFilter('All');
        setReportData(null);
        setFetchError(null);
    };

    const selectedReportInfo = REPORT_OPTIONS.find(r => r.id === reportType);

    // --- Conditional Filter Rendering ---
    const renderFilters = () => {
        return (
            <>
                <div className="filter-group date-range">
                    <label><FaCalendarAlt /> Date Range</label>
                    <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} title="Start Date"/>
                    <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} title="End Date"/>
                </div>

                {(reportType === 'orders' || reportType === 'sales') && (
                    <div className="filter-group">
                        <label>Order Status</label>
                        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                            {STATUS_OPTIONS.map(status => (<option key={status} value={status}>{status}</option>))}
                        </select>
                    </div>
                )}

                {(reportType === 'products' || reportType === 'inventory' || reportType === 'reviews') && (
                    <div className="filter-group">
                        <label>Product/Category ID (Optional)</label>
                        <input type="text" value={productFilter} onChange={(e) => setProductFilter(e.target.value)} placeholder="e.g., T-Shirt or Category ID"/>
                    </div>
                )}
                
                {reportType === 'users' && (
                    <div className="filter-group">
                        <label>User Role</label>
                        <select value={userRoleFilter} onChange={(e) => setUserRoleFilter(e.target.value)}>
                            {ROLE_OPTIONS.map(role => (<option key={role} value={role}>{role}</option>))}
                        </select>
                    </div>
                )}
            </>
        );
    };

    // --- Report Data Display ---
    const renderReportData = () => {
        if (!reportData) return null;

        if (reportType === 'sales') {
            return (
                <>
                    <p><span>Total Revenue:</span> Rs. {parseFloat(reportData.totalRevenue).toLocaleString()}.00</p>
                    <p><span>Total Orders:</span> {reportData.totalOrders}</p>
                    <p><span>Avg. Order Value (AOV):</span> Rs. {parseFloat(reportData.avgOrderValue).toLocaleString()}.00</p>
                    <p><span>Period Covered:</span> {reportData.period}</p>
                    <p className="chart-placeholder">Placeholder for Revenue Trend Chart</p>
                </>
            );
        }
        
        if (reportType === 'inventory') {
            return (
                <>
                    <p><span>Total Product SKUs:</span> {reportData.totalSKUs}</p>
                    <p><span>Total Units in Stock:</span> {reportData.totalUnits}</p>
                    
                    {/* CORRECTED: Cleaned up the surrounding JSX to prevent the compile error */}
                    <p><span>Low Stock Items (&lt;= 10 units):</span> <span className="highlight-danger">{reportData.lowStockCount}</span></p>
                    
                    <p><span>Data Snapshot Date:</span> {reportData.updated}</p>
                    
                    {Array.isArray(reportData.lowStockList) && reportData.lowStockList.length > 0 && (
                        <div className="product-rankings" style={{ marginTop: '20px' }}>
                            <h3>Details: {reportData.lowStockList.length} Items Below Threshold</h3>
                            {reportData.lowStockList.map((p, i) => (
                                <p key={i}><span>{p.name}:</span> <span className="highlight-danger">{p.count} units left</span></p>
                            ))}
                        </div>
                    )}
                </>
            );
        }

        if (reportType === 'products') {
            return (
                <div className="product-rankings">
                    <h3>Top Selling Items by Revenue</h3>
                    {reportData.map((p, i) => (
                        <p key={i}><span>{i + 1}. {p.name}:</span> {p.units} units (Rs. {parseFloat(p.revenue).toLocaleString()}.00)</p>
                    ))}
                </div>
            );
        }

        if (reportType === 'orders') {
            return (
                <div className="orders-table">
                    <h3>Detailed Order List ({reportData.length} Results)</h3>
                    <div className="order-row header"><span>Order #</span><span>Customer</span><span>Date</span><span>Total</span><span>Status</span></div>
                    {reportData.map(order => (
                        <div key={order.id} className="order-row detail-row">
                            <span>#{order.id}</span>
                            <span>{order.user}</span>
                            <span>{order.date}</span>
                            <span>Rs. {parseFloat(order.total).toLocaleString()}.00</span>
                            <span className={`status-${order.status.toLowerCase()}`}>{order.status}</span>
                        </div>
                    ))}
                </div>
            );
        }

        if (reportType === 'users') {
             return (
                <div className="orders-table user-table">
                    <h3>{userRoleFilter} Accounts ({reportData.length} Total)</h3>
                    <div className="order-row header"><span>Name</span><span>Email</span><span>Role</span><span>Joined</span></div>
                    {reportData.map(user => (
                        <div key={user.email} className="order-row">
                            <span>{user.name}</span>
                            <span>{user.email}</span>
                            <span className={`role-${user.role.toLowerCase()}`}>{user.role}</span>
                            <span>{user.created}</span>
                        </div>
                    ))}
                </div>
            );
        }

        if (reportType === 'reviews') {
             return (
                <>
                    <p><span>Overall Average Rating:</span> <span className="highlight-success">{parseFloat(reportData.avgRating).toFixed(1)} / 5.0</span></p>
                    <p><span>Total Reviews Counted:</span> {reportData.totalReviews}</p>
                    
                    <div className="product-rankings" style={{ marginTop: '20px' }}>
                        <h3>Ratings by Product</h3>
                        {Array.isArray(reportData.productRatings) && reportData.productRatings.map((p, i) => (
                            <p key={i}><span>{p.name}:</span> {p.rating} / 5.0</p>
                        ))}
                    </div>
                </>
            );
        }

        return <p>No specific display template for this report type.</p>;
    };

    return (
        <div className="reports-page">
            <h1 className="reports-title">Analytics & Reports Dashboard</h1>
            
            {/* Report Configuration Panel */}
            <div className="report-config-panel">
                <div className="report-type-selector">
                    {REPORT_OPTIONS.map(option => (
                        <button
                            key={option.id}
                            className={`type-button ${reportType === option.id ? 'active' : ''}`}
                            onClick={() => { setReportType(option.id); setReportData(null); setFetchError(null); }}
                            title={option.description}
                        >
                            <span className="type-icon">{option.icon}</span>
                            <span className="type-name">{option.name}</span>
                        </button>
                    ))}
                </div>

                <div className="report-filters-wrapper">
                    <h2><FaFilter /> Filters</h2>
                    <div className="report-filters">
                        {renderFilters()}
                        <div className="action-buttons">
                            <button 
                                className="btn-generate" 
                                onClick={handleGenerateReport} 
                                disabled={isGenerating}
                            >
                                {isGenerating ? 'Generating...' : 'Generate Report'}
                            </button>
                            <button 
                                className="btn-reset" 
                                onClick={handleResetFilters} 
                                disabled={isGenerating}
                            >
                                <FaRedo /> Reset
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Report Output Panel */}
            <div className="report-output-panel">
                <h2>{isGenerating ? 'Generating Report...' : `Report: ${selectedReportInfo.name}`}</h2>

                {isGenerating && (
                    <div className="report-status loading-state">
                        <div className="spinner"></div>
                        <p>Fetching and compiling data...</p>
                    </div>
                )}
                
                {fetchError && !isGenerating && (
                    <div className="report-status error-state">
                        <p>Error: {fetchError}</p>
                        <p>Could not fetch data from the server. Check console for details.</p>
                    </div>
                )}

                {reportData && !isGenerating && !fetchError && (
                    <div className="report-data-view">
                        <button className="btn-download" onClick={handleDownloadPdf}>
                            <FaFilePdf /> Download PDF
                        </button>

                        <div className="report-summary">
                            {renderReportData()}
                        </div>
                    </div>
                )}
                
                {!reportData && !isGenerating && !fetchError && (
                     <div className="report-status empty-state">
                         <p>Select a report type and filters, then click "Generate Report" to view results.</p>
                     </div>
                )}
            </div>
        </div>
    );
}

export default Reports;
